// Раскладка вложений по колонкам бара и взаимное гашение каналов. Запрос
// исполняет ОДИН провайдер, поэтому канал, которого никто не покрывает вместе с
// уже заполненными, гасится прямо в баре — вместо отказа цены и непонятной
// кнопки на отправке. Причину считает `prompt-channel-gates`, здесь она только
// переводится и раскладывается по колонкам.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { promptChannelHints } from "./prompt-channel-gates";

import type { ImageInputRole, ModeConfig } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

export interface UsePromptChannelsParams {
    currentMode: ModeConfig | null;
    /** Вложения кадрового пула — всё, что не референс. */
    frameItems: readonly PendingUpload[];
    hasImageRoles: boolean;
    imageRoles: readonly ImageInputRole[];
    itemByRole: Partial<Record<ImageInputRole, PendingUpload>>;
    referenceImageCount: number;
    selectedElementCount: number;
    videoAttachmentCount: number;
    audioEnabled: boolean;
}

export interface UsePromptChannelsResult {
    videoItems: PendingUpload[];
    audioItems: PendingUpload[];
    maxInputVideos: number;
    maxInputAudio: number;
    /** Что показывает лента пула — вложения без своей колонки. */
    poolItems: PendingUpload[];
    /** Готовые подсказки «почему канал недоступен»; пусто — канал доступен. */
    framesHint: string | undefined;
    elementsHint: string | undefined;
    referencesHint: string | undefined;
}

export function usePromptChannels({
    currentMode,
    frameItems,
    hasImageRoles,
    imageRoles,
    itemByRole,
    referenceImageCount,
    selectedElementCount,
    videoAttachmentCount,
    audioEnabled,
}: UsePromptChannelsParams): UsePromptChannelsResult {
    const { t } = useTranslation("chat");

    // Кадры в режиме с роль-слотами живут в своих слотах, поэтому лента там несёт
    // только вложения других видов — видео и аудио; картинка без роли туда не
    // попадает намеренно: она в запрос не уедет (`orderImageUrlsByRole` берёт
    // кадры по ролям), и показывать её равноправной плиткой значило бы обещать
    // отправку, которой не будет. Видео и звук уходят в свои колонки ряда входов
    // — снимок различает их собственными полями запроса, и в баре они обязаны
    // называться так же, а не безымянной лентой «прочих файлов».
    const videoItems = useMemo(() => frameItems.filter((item) => item.kind === "video"), [frameItems]);
    const audioItems = useMemo(() => frameItems.filter((item) => item.kind === "audio"), [frameItems]);
    const maxInputVideos = currentMode?.maxInputVideos ?? 0;
    const maxInputAudio = currentMode?.maxInputAudio ?? 0;

    const poolItems = useMemo(
        () =>
            (hasImageRoles ? frameItems.filter((item) => item.kind !== "image") : frameItems).filter(
                // Своя колонка уже показывает файл; вторая плитка была бы не тем же
                // входом, а видимостью двух.
                (item) =>
                    !(item.kind === "video" && maxInputVideos > 0) &&
                    !(item.kind === "audio" && maxInputAudio > 0),
            ),
        [hasImageRoles, frameItems, maxInputVideos, maxInputAudio],
    );

    const channelHints = useMemo(
        () =>
            promptChannelHints(currentMode, {
                filledFrameRoles: imageRoles.filter((role) => itemByRole[role] !== undefined).length,
                selectedElements: selectedElementCount,
                referenceImages: referenceImageCount,
                videoAttachments: videoAttachmentCount,
                audioEnabled,
            }),
        [
            currentMode,
            imageRoles,
            itemByRole,
            selectedElementCount,
            referenceImageCount,
            videoAttachmentCount,
            audioEnabled,
        ],
    );

    const hints = useMemo(() => {
        const hintText = (hintKey: string | null) =>
            hintKey === null ? undefined : (t(hintKey) ?? undefined);
        return {
            framesHint: hintText(channelHints.frames),
            elementsHint: hintText(channelHints.elements),
            referencesHint: hintText(channelHints.references),
        };
    }, [channelHints, t]);

    return { videoItems, audioItems, maxInputVideos, maxInputAudio, poolItems, ...hints };
}
